//  ----------      START   IMPORT  BLOCK       ----------
import { useState, useRef, useCallback, useMemo,  } from "react";
//  Widgets
import QualificationForm from "../ProfessionalMenu/SubMenus/QualificationsForm";
import WidgetSkillsSelector from "../ProfessionalMenu/SubMenus/WidgetSkillsSelector";
import WorkingHoursForm from "../ProfessionalMenu/SubMenus/WorkingHoursForm";
import { Container, Button, ProgressBar, Alert } from "react-bootstrap";
//  Services
import RegisterService from "../../API/Services/RegisterService";
//  Context
import { useLanguage } from "../../API/Context/LanguageContext";
//  Config
import {VocabButtons as btn_vocab} from "../../ConfigFiles/multilingual/VocabButtons";
import VocabRegisterProfessional from "../../ConfigFiles/multilingual/VocabRegisterProfessional";
//  ----------      END   IMPORT  BLOCK       ----------


const professionalService = {
    register: async (data) => {
        const register_service = new RegisterService
        // Now using the formatted data
        register_service.register_professional(data)
    }
};

// ------------------------

// Define the steps (3 steps)
const STEPS = [
    { id: 0, title: 'Qualifications', Component: QualificationForm, key: 'qualifications', defaultType: 'array' },
    { id: 1, title: 'Working Hours', Component: WorkingHoursForm, key: 'workingHours', defaultType: 'object' },
    { id: 2, title: 'Skills', Component: WidgetSkillsSelector, key: 'skills', defaultType: 'array' },
];

/**
 * Formats the raw data from component states into the structure expected by the API.
 * * @param {Array} qualificationsData - Array of qualification objects (includes deleted/unformatted data).
 * @param {Array} skillsData - Array of skill objects ({skill_id, name, deleted, etc.})
 * @param {Object} workingHoursData - Object containing manage_agenda, schedule_visible, and weekly_schedule.
 * @returns {Object} The API-compliant final data object.
 */
const formatDataForAPI = (qualificationsData, skillsData, workingHoursData) => {
    
    // 1. Process Qualifications
    // NOTE: qualificationsData should already be partially formatted by QualificationForm,
    // but we ensure non-null/default values are used.
    const formattedQualifications = (qualificationsData || []).map(q => ({
        id: q.id || null, // Keep existing ID or null/default
        title: q.title || "",
        category: q.category || 0,
        license: q.license || "",
        institution: q.institution || "",
        year_of_completion: q.year_of_completion || 0,
        deleted: !!q.deleted // Ensure deleted is a boolean
    }));
    
    // 2. Process Skills (API expects an array of simple string IDs/Names)
    // Assuming the skill objects returned by WidgetSkillsSelector contain a string key 
    // that the API recognizes as the skill ID/name.
    const formattedSkills = (skillsData || [])
        .filter(s => !s.deleted)
        .map(s => s.skill_id || s.name || s.id || "unknown_skill"); // Use skill_id, name, or id as the string value

    // 3. Process Working Hours
    // Assuming workingHoursData contains { manage_agenda, schedule_visible, weekly_schedule }
    // and weekly_schedule is what maps to new_schedule.
    const formattedWorkingHours = {
        new_schedule: workingHoursData?.weekly_schedule || [], // Use the weekly_schedule property
        manage_agenda: workingHoursData?.manage_agenda ?? false,
        schedule_visible: workingHoursData?.schedule_visible ?? false,
    };

    return {
        qualifications_params: formattedQualifications,
        skills: formattedSkills,
        working_hours: formattedWorkingHours
    };
};

const RegisterProfessionalPage = ({  }) => {
    //  Consume context
    const { lan } = useLanguage()
    // 1. Core Stepper State
    const [currentStep, setCurrentStep] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submissionStatus, setSubmissionStatus] = useState(null);

    // 2. Dedicated Data States
    const [qualificationsData, setQualificationsData] = useState(null); 
    const [workingHoursData, setWorkingHoursData] = useState(null);   
    const [skillsData, setSkillsData] = useState(null); 
    
    // 3. Mapping the key to its state setter/getter using useMemo
    const stateMap = useMemo(() => ({
        qualifications: { 
            data: qualificationsData, 
            setter: setQualificationsData 
        },
        workingHours: { 
            data: workingHoursData, 
            setter: setWorkingHoursData 
        },
        skills: { 
            data: skillsData,
            setter: setSkillsData
        }
    }), [qualificationsData, workingHoursData, skillsData]);

    const currentWidgetRef = useRef(null);

    // 4. Data Change Handler
    const handleChildDataChange = useCallback((key, data) => {
        const setter = stateMap[key]?.setter;
        if (setter) {
            setter(data);
        }
    }, [stateMap]);

    // --- Current Step Details (Calculated on every render) ---
    const currentStepDetail = STEPS[currentStep];

    if (!currentStepDetail) {
        return <Container className="my-5">Error: Invalid Step.</Container>;
    }
    
    const { key: currentStepKey, defaultType } = currentStepDetail;
    const currentStepData = stateMap[currentStepKey].data;
    const isCurrentStepValid = currentStepData !== null; 

    // Determine the safe initial data to pass to the child component
    const initialDataForCurrentStep = currentStepData 
        || (defaultType === 'array' ? [] : {}); 


    // 5. Navigation and Submission Logic
    const handleNext = () => {
        if (isCurrentStepValid) {
            if (currentStep < STEPS.length - 1) {
                setCurrentStep(prev => prev + 1);
            }
        } else {
            setSubmissionStatus({ success: false, message: VocabRegisterProfessional.error_incomplete[lan] });
        }
    };

    const handleSubmit = async () => {
        // Final check for all steps
        if (qualificationsData === null || workingHoursData === null || skillsData === null) {
             setSubmissionStatus({ success: false, message: "Please complete all steps." });
             return;
        }

        setIsSubmitting(true);
        setSubmissionStatus(null);
        
        // 🎯 CALL THE NEW FORMATTING FUNCTION HERE
        const finalData = formatDataForAPI(
            qualificationsData, 
            skillsData, 
            workingHoursData
        );

        const result = await professionalService.register(finalData);
        
        setIsSubmitting(false);
        setSubmissionStatus(result);
    };

    // 6. Render
    const progressPercent = ((currentStep + 1) / STEPS.length) * 100;
    const CurrentComponent = currentStepDetail.Component;

    
    return (
        <Container className="my-5">
            <h1>{VocabRegisterProfessional.register_title[lan]}</h1>
            
            {/* Progress Bar with numerical display */}
            <ProgressBar 
                now={progressPercent} 
                label={`${currentStep + 1}/${STEPS.length}`} 
                className="mb-4" 
            />
            
            <div className="p-4 border rounded shadow-sm">
                <h2>{currentStepDetail.title}</h2>

                {/* Render the current step component */}
                <CurrentComponent
                    key={currentStepKey} 
                    ref={currentWidgetRef}
                    lan={lan}
                    initialData={initialDataForCurrentStep} 
                    onChange={(data) => handleChildDataChange(currentStepKey, data)}
                />

                {/* Status/Error Alert */}
                {submissionStatus && (
                    <Alert variant={submissionStatus.success ? 'success' : 'danger'} className="mt-3">
                        {submissionStatus.message}
                    </Alert>
                )}

                {/* Navigation Buttons */}
                <div className="d-flex justify-content-between mt-4">
                    <Button 
                        variant="secondary" 
                        onClick={() => setCurrentStep(prev => prev - 1)} 
                        disabled={currentStep === 0 || isSubmitting}
                    >
                        {btn_vocab.back[lan]}
                    </Button>

                    {currentStep < STEPS.length - 1 ? (
                        <Button 
                            variant="primary" 
                            onClick={handleNext} 
                            disabled={!isCurrentStepValid || isSubmitting}
                        >
                            {btn_vocab.next[lan]}
                        </Button>
                    ) : (
                        <Button 
                            variant="success" 
                            onClick={handleSubmit} 
                            disabled={!isCurrentStepValid || isSubmitting}
                        >
                            {/* Hardened vocabulary access */}
                            {btn_vocab.submit[lan]}
                        </Button>
                    )}
                </div>
            </div>
        </Container>
    );
};

export default RegisterProfessionalPage;